import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getConsent, setConsent, applyConsent } from '../../utils/cookieConsent';

/**
 * PDPL cookie-consent banner. Shows on first visit until the user decides.
 * Necessary cookies always run; analytics/marketing load only on "Accept all".
 * On later visits it re-applies the stored decision (e.g. loads GA if consented)
 * and stays hidden.
 */
export default function CookieConsentBanner() {
  const { i18n } = useTranslation();
  const ar = (i18n.language || '').toLowerCase().startsWith('ar');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const existing = getConsent();
    if (existing) {
      applyConsent(existing);
    } else {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    setConsent(true, true);
    setVisible(false);
  };
  const necessaryOnly = () => {
    setConsent(false, false);
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={ar ? 'إشعار ملفات تعريف الارتباط' : 'Cookie notice'}
      dir={ar ? 'rtl' : 'ltr'}
      className="fixed inset-x-0 bottom-0 z-[9999] px-4 pb-4"
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-3 rounded-2xl border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-strong)] p-4 shadow-[0_2px_4px_rgba(0,77,64,0.06),0_16px_36px_-14px_rgba(0,77,64,0.3)] dark:border-white/10 dark:bg-[#062B25] sm:flex-row sm:items-center sm:gap-4">
        <p className="m-0 flex-1 text-[13.5px] leading-relaxed text-[var(--pv-color-text)] dark:text-gray-200">
          {ar
            ? 'نستخدم ملفات ضرورية لتشغيل المنصة، وملفات تحليلية وتسويقية بموافقتك لتحسين الخدمة. يمكنك القبول أو الاكتفاء بالضروري.'
            : 'We use necessary cookies to run the platform, and analytics/marketing cookies with your consent to improve it. You can accept all or keep only the necessary ones.'}{' '}
          <Link to="/privacy" className="font-semibold text-[#004D40] underline dark:text-[#7FD6C3]">
            {ar ? 'سياسة الخصوصية' : 'Privacy Policy'}
          </Link>
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={necessaryOnly}
            className="rounded-xl border border-[var(--pv-border-soft)] px-4 py-2 text-[13.5px] font-semibold text-[var(--pv-color-text)] hover:bg-[var(--pv-color-surface-sunken)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#004D40] dark:border-gray-600 dark:text-gray-200 dark:hover:bg-white/5"
          >
            {ar ? 'الضروري فقط' : 'Necessary only'}
          </button>
          <button
            type="button"
            onClick={acceptAll}
            className="rounded-xl bg-[#004D40] px-4 py-2 text-[13.5px] font-semibold text-white hover:bg-[#003930] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#004D40]"
          >
            {ar ? 'قبول الكل' : 'Accept all'}
          </button>
        </div>
      </div>
    </div>
  );
}
